import React, { useEffect, useMemo, useRef, useState } from "react";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import "../styles/Dashboard.css";
import { deleteData, getData, postData, putData } from "../services/Request";
import { toast } from "react-toastify";
import { useLoader } from "../context/LoaderContext";

const emptyForm = {
  name: "",
  email: "",
  password: "",
  sellerId: "",
  role: "user",
  products: false,
  quotation: false,
  invoices: false,
};

const getUserId = (user) =>
  user?.id ?? user?._id ?? user?.user_id ?? user?.userId ?? user?.email ?? "";

const normaliseList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.rows)) return payload.data.rows;
  if (Array.isArray(payload?.users)) return payload.users;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const hasValue = (value) => `${value ?? ""}`.trim().length > 0;

export default function Users() {
  const { showLoader, hideLoader } = useLoader();
  const [users, setUsers] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [formData, setFormData] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [listLoading, setListLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const hasFetchedInitial = useRef(false);
  const isFormComplete =
    hasValue(formData.name) &&
    hasValue(formData.email) &&
    hasValue(formData.sellerId) &&
    (editingId ? true : hasValue(formData.password));

  const fetchUsers = async ({ withLoader = false } = {}) => {
    try {
      if (withLoader) showLoader();
      else setListLoading(true);
      const response = await getData("api/users");
      setUsers(normaliseList(response));
    } catch (error) {
      toast.error("Users not loaded.");
      console.error("Failed to fetch users", error);
    } finally {
      if (withLoader) hideLoader();
      else setListLoading(false);
    }
  };

  const fetchSellers = async () => {
    try {
      const response = await getData("api/seller");
      setSellers(normaliseList(response));
    } catch (error) {
      toast.error("Sellers not loaded.");
      console.error("Failed to fetch sellers", error);
    }
  };

  useEffect(() => {
    if (hasFetchedInitial.current) return;
    hasFetchedInitial.current = true;
    (async () => {
      showLoader();
      await Promise.all([fetchUsers(), fetchSellers()]);
      hideLoader();
    })();
  }, []);

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    const nextValue = type === "checkbox" ? Boolean(checked) : value;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "email" ? nextValue.trim().toLowerCase() : nextValue,
    }));
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingId(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (
      !formData.name ||
      !formData.email ||
      (!editingId && !formData.password) ||
      !formData.sellerId
    ) {
      toast.error("All fields are required (password needed for new users).");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...formData, role: formData.role || "user" };
      if (editingId && !payload.password) {
        delete payload.password;
      }
      if (editingId) {
        await putData(`api/users/${editingId}`, payload);
        toast.success("User updated.");
      } else {
        await postData("api/register", payload);
        toast.success("User created.");
      }
      resetForm();
      await fetchUsers();
    } catch (error) {
      const action = editingId ? "update" : "create";
      toast.error(`Failed to ${action} user.`);
      console.error("Submit user failed", error);
    } finally {
      setSubmitting(false);
    }
  };

  const loadUserForEdit = (userId) => {
    const found = users.find((u) => String(getUserId(u)) === String(userId));
    if (!found) {
      toast.error("User not found.");
      return;
    }
    setFormData({
      ...emptyForm,
      ...found,
      password: "",
      sellerId: found?.sellerId ?? found?.seller_id ?? found?.seller ?? "",
      role: found?.role || "user",
      products: Boolean(found?.products),
      quotation: Boolean(found?.quotation),
      invoices: Boolean(found?.invoices),
    });
    setEditingId(userId);
  };

  const handleDelete = async (userId) => {
    const targetId = userId || confirmDeleteId;
    if (!targetId) {
      toast.error("User ID missing.");
      return;
    }
    const toastId = toast.loading("Deleting user...");
    try {
      showLoader();
      await deleteData(`api/users/${targetId}`);
      toast.update(toastId, {
        render: "User deleted.",
        type: "success",
        isLoading: false,
        autoClose: 1500,
      });
      if (editingId === targetId) resetForm();
      await fetchUsers();
    } catch (error) {
      toast.update(toastId, {
        render: "User could not be deleted. Please try again.",
        type: "error",
        isLoading: false,
        autoClose: 2000,
      });
      console.error("Failed to delete user", error);
    } finally {
      hideLoader();
      setConfirmDeleteId(null);
    }
  };

  const openDeleteModal = (userId) => setConfirmDeleteId(userId);
  const closeDeleteModal = () => setConfirmDeleteId(null);

  const sellerOptions = useMemo(
    () =>
      sellers.map((s) => ({
        id: s?.id ?? s?._id ?? s?.seller_id ?? s?.sellerId ?? "",
        label: s?.address || s?.seller_name || s?.business_name || "Seller",
      })),
    [sellers]
  );

  return (
    <div className="app-shell">
      <Sidebar />
      <div className="app-content">
        <Navbar />
        <div className="dashboard-page">
          <main className="dashboard">
            <section className="sample-page">
              <div className="sample-page__header">
                <h1>Users</h1>
              </div>

              <div className="seller-management">
                <div className="seller-card seller-form-card">
                  <div className="seller-card__header">
                    <h3>{editingId ? "Update User" : "Add User"}</h3>
                    {editingId && (
                      <button
                        type="button"
                        className="text-link-btn"
                        onClick={resetForm}
                      >
                        Cancel edit
                      </button>
                    )}
                  </div>
                  <form className="seller-form" onSubmit={handleSubmit}>
                    <div className="form-grid">
                      <label className="form-field">
                        <span>
                          Name <span className="requied">*</span>
                        </span>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          placeholder="User name"
                        />
                      </label>

                      <label className="form-field">
                        <span>
                          Email <span className="requied">*</span>
                        </span>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          placeholder="user@example.com"
                        />
                      </label>

                      <label className="form-field">
                        <span>
                          {editingId ? "Password (optional)" : "Password "}
                          <span className="requied">*</span>
                        </span>
                        <input
                          type="password"
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          placeholder={
                            editingId
                              ? "Leave blank to keep current"
                              : "Enter password"
                          }
                          required={!editingId}
                        />
                      </label>

                      <label className="form-field">
                        <span>
                          Seller (address) <span className="requied">*</span>
                        </span>
                        <select
                          name="sellerId"
                          value={formData.sellerId}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="">Select seller</option>
                          {sellerOptions.map((seller) => (
                            <option key={seller.id} value={seller.id}>
                              {seller.label}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="form-field">
                        <span>
                          Role<span className="requied">*</span>
                        </span>
                        <select
                          name="role"
                          value={formData.role}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="user">User</option>
                          <option value="admin">Admin</option>
                        </select>
                      </label>

                      {formData.role === "user" && (
                        <div className="form-field">
                          <span>Access</span>
                          <div
                            style={{
                              display: "flex",
                              gap: "12px",
                              flexWrap: "wrap",
                            }}
                          >
                            <label style={{ display: "flex", gap: "6px" }}>
                              <input
                                type="checkbox"
                                name="products"
                                checked={formData.products}
                                onChange={handleInputChange}
                              />
                              <span>Products</span>
                            </label>
                            <label style={{ display: "flex", gap: "6px" }}>
                              <input
                                type="checkbox"
                                name="quotation"
                                checked={formData.quotation}
                                onChange={handleInputChange}
                              />
                              <span>Quotation</span>
                            </label>
                            <label style={{ display: "flex", gap: "6px" }}>
                              <input
                                type="checkbox"
                                name="invoices"
                                checked={formData.invoices}
                                onChange={handleInputChange}
                              />
                              <span>Invoices</span>
                            </label>
                          </div>
                        </div>
                      )}
                    </div>

                    <button
                      type="submit"
                      className="primary-btn"
                      disabled={submitting || !isFormComplete}
                    >
                      {submitting
                        ? "Please wait..."
                        : editingId
                        ? "Update User"
                        : "Add User"}
                    </button>
                  </form>
                </div>

                <div className="seller-card seller-table-card">
                  <div className="seller-card__header">
                    <h3>User list</h3>
                    <button
                      type="button"
                      className="text-link-btn"
                      onClick={() => fetchUsers()}
                      disabled={listLoading}
                    >
                      {listLoading ? "Refreshing..." : "Refresh"}
                    </button>
                  </div>
                  <div className="seller-table-wrapper">
                    <table className="seller-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Seller</th>
                          <th>Role</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.length === 0 && (
                          <tr>
                            <td colSpan="5" className="empty-state">
                              {listLoading
                                ? "Loading users..."
                                : "No users found."}
                            </td>
                          </tr>
                        )}
                        {users.map((user, index) => {
                          const userId = getUserId(user);
                          const rowKey =
                            userId || `${user?.email ?? "row"}-${index}`;
                          const sellerLabel = sellerOptions.find(
                            (s) =>
                              String(s.id) ===
                              String(user?.sellerId ?? user?.seller_id)
                          )?.label;
                          return (
                            <tr key={rowKey}>
                              <td>{user?.name || "-"}</td>
                              <td>{user?.email || "-"}</td>
                              <td>{sellerLabel || "-"}</td>
                              <td>{user?.role || "user"}</td>
                              <td>
                                <div className="table-actions">
                                  <button
                                    type="button"
                                    onClick={() => loadUserForEdit(userId)}
                                    disabled={!userId}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    className="danger"
                                    onClick={() => openDeleteModal(userId)}
                                    disabled={!userId}
                                  >
                                    Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {confirmDeleteId && (
                      <div className="modal-backdrop">
                        <div className="modal-card">
                          <h3>Delete user</h3>
                          <p>This action cannot be undone. Delete this user?</p>
                          <div className="modal-actions">
                            <button
                              type="button"
                              className="outline-btn"
                              onClick={closeDeleteModal}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              className="primary-btn danger"
                              onClick={() => handleDelete(confirmDeleteId)}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>
          </main>
        </div>
      </div>
    </div>
  );
}
